import type { NavigateFunction } from "react-router-dom";

const SESSION_KEY = "pabbit_address_object_search_draft";

export type ObjectConfigureLocationState = {
  objectSearchFromStatus?: boolean;
};

/** 都道府県・市区町村・丁目番地など住所型の物件検索フォーム（PabbitLite / PATMO / Dearis で共用） */
export interface AddressObjectSearchDraft {
  objectName: string;
  prefecture_name: string;
  city_name: string;
  rest_address: string;
}

const EMPTY_ADDRESS_OBJECT_SEARCH: AddressObjectSearchDraft = {
  objectName: "",
  prefecture_name: "",
  city_name: "",
  rest_address: "",
};

export function isObjectSearchFromStatus(locationState: unknown): boolean {
  return (
    typeof locationState === "object" &&
    locationState !== null &&
    (locationState as ObjectConfigureLocationState).objectSearchFromStatus === true
  );
}

export function readAddressObjectSearchDraft(): AddressObjectSearchDraft | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    const o = parsed as Record<string, unknown>;
    if (
      typeof o.objectName !== "string" ||
      typeof o.prefecture_name !== "string" ||
      typeof o.city_name !== "string" ||
      typeof o.rest_address !== "string"
    ) {
      return null;
    }
    return {
      objectName: o.objectName,
      prefecture_name: o.prefecture_name,
      city_name: o.city_name,
      rest_address: o.rest_address,
    };
  } catch {
    return null;
  }
}

export function writeAddressObjectSearchDraft(values: AddressObjectSearchDraft) {
  sessionStorage.setItem(SESSION_KEY, JSON.stringify(values));
}

export function clearAddressObjectSearchDraft() {
  sessionStorage.removeItem(SESSION_KEY);
}

/** 初回表示用：`/status` 経由なら空、それ以外は session ドラフト */
export function getAddressObjectSearchFormDefaults(
  locationState: unknown
): AddressObjectSearchDraft {
  if (isObjectSearchFromStatus(locationState)) return EMPTY_ADDRESS_OBJECT_SEARCH;
  return readAddressObjectSearchDraft() ?? EMPTY_ADDRESS_OBJECT_SEARCH;
}

/** `/status` から「物件設定」へ：検索画面でドラフトを使わないよう location.state を付与 */
export function navigateToObjectConfigureFromStatus(navigate: NavigateFunction) {
  navigate("/configure", { state: { objectSearchFromStatus: true } satisfies ObjectConfigureLocationState });
}

export function addressObjectSearchFormToResultsQuery(values: AddressObjectSearchDraft): string {
  const sp = new URLSearchParams();
  sp.set("prefecture", values.prefecture_name);
  sp.set("city", values.city_name);
  sp.set("address", values.rest_address);
  sp.set("name", values.objectName);
  return sp.toString();
}

export function persistAddressObjectSearchAndGoToResults(
  navigate: NavigateFunction,
  values: AddressObjectSearchDraft
): void {
  writeAddressObjectSearchDraft(values);
  navigate(`/configure/results?${addressObjectSearchFormToResultsQuery(values)}`);
}
